import { ProductionStatus } from "@enums/productionStatus.enum";
import { ToolroomMachineStatus } from "@helpers/toolroomMachineStatus";

// The machine time band: one row per day across a fixed window, coloured by production status. The window and the
// colours match the nightly Employee Stat Report PDF (API: Helpers/Pdf/EmployeeStatReportPdf.ColourFor), so the
// screen and the emailed report read the same. Deliberately finer than GetProductionStatusColor, which puts
// changeover and running in the same green - on a timeline those have to be told apart.
export const MACHINE_TIME_START_HOUR = 5;
export const MACHINE_TIME_END_HOUR = 19;

export function MachineTimeColour(status: ProductionStatus): string {
	switch (status) {
		case ProductionStatus.Active:
			return "#16a34a";
		case ProductionStatus.Changeover:
			return "#ea580c";
		case ProductionStatus.QualityCheck:
		case ProductionStatus.QLoop:
			return "#0284c7";
		case ProductionStatus.Break:
		case ProductionStatus.ToiletBreak:
		case ProductionStatus.ToolTipChange:
		case ProductionStatus.SwarfCleanOut:
			return "#ca8a04";
		case ProductionStatus.Breakdown:
		case ProductionStatus.AssistanceRequired:
		case ProductionStatus.InitialCheckFailed:
		case ProductionStatus.Maintenance:
			return "#b91c1c";
		default:
			return "#9ca3af";
	}
}

export const MACHINE_TIME_LEGEND = [
	{ colour: MachineTimeColour(ProductionStatus.Active), label: "Running" },
	{ colour: MachineTimeColour(ProductionStatus.Changeover), label: "Changeover" },
	{ colour: MachineTimeColour(ProductionStatus.QualityCheck), label: "Checks" },
	{ colour: MachineTimeColour(ProductionStatus.Break), label: "Breaks" },
	{ colour: MachineTimeColour(ProductionStatus.Breakdown), label: "Stopped" },
	{ colour: MachineTimeColour(ProductionStatus.Idle), label: "Idle" }
];

// Two-hourly marks across the window, as percentages, so the ruler lines up with the bars.
export const MACHINE_TIME_TICKS = Array.from(
	{ length: Math.ceil((MACHINE_TIME_END_HOUR - MACHINE_TIME_START_HOUR) / 2) },
	(_unused, index) => {
		const hour = MACHINE_TIME_START_HOUR + index * 2;
		return {
			label: `${String(hour).padStart(2, "0")}:00`,
			percent: ((hour - MACHINE_TIME_START_HOUR) / (MACHINE_TIME_END_HOUR - MACHINE_TIME_START_HOUR)) * 100
		};
	}
);

// Each status spell as a percentage across 05:00-19:00, so the bars line up with the ruler above them.
export function MachineTimeBars(day: any) {
	const windowStart = new Date(day.day);
	windowStart.setHours(MACHINE_TIME_START_HOUR, 0, 0, 0);
	const windowMinutes = (MACHINE_TIME_END_HOUR - MACHINE_TIME_START_HOUR) * 60;
	const minutesIn = (value: string) => (new Date(value).getTime() - windowStart.getTime()) / 60000;

	const time = (value: string) => new Date(value).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

	return (day.segments || []).map((segment: any) => {
		const from = Math.max(0, minutesIn(segment.from));
		const to = Math.min(windowMinutes, minutesIn(segment.to));
		return {
			colour: MachineTimeColour(segment.status),
			duration: MachineTimeLength(segment.minutes),
			left: (from / windowMinutes) * 100,
			machineName: segment.machineName,
			operation: segment.operationNumber ? `${segment.operationNumber}${segment.workOrderId ? " \u00b7 WO " + segment.workOrderId : ""}` : "",
			// The same words the toolroom boards use, rather than a second list of status names.
			status: ToolroomMachineStatus(segment.status).label,
			times: `${time(segment.from)} \u2013 ${time(segment.to)}`,
			width: Math.max(0.2, ((to - from) / windowMinutes) * 100)
		};
	}).filter((bar: any) => bar.width > 0);
}

// Where the clocked-in window sits on the band, so the shift reads as a span rather than as two times in the
// margin. Clock out can be unknown, in which case the window runs to the last status of the day.
export function MachineTimeClock(day: any) {
	const time = (value: string) => new Date(value).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
	if (!day.clockIn) {
		return { inLabel: "-", left: 0, outLabel: "-", show: false, source: "", width: 0 };
	}

	const windowStart = new Date(day.day);
	windowStart.setHours(MACHINE_TIME_START_HOUR, 0, 0, 0);
	const windowMinutes = (MACHINE_TIME_END_HOUR - MACHINE_TIME_START_HOUR) * 60;
	const minutesIn = (value: string) => (new Date(value).getTime() - windowStart.getTime()) / 60000;

	const from = Math.max(0, minutesIn(day.clockIn));
	const to = Math.min(windowMinutes, day.clockOut ? minutesIn(day.clockOut) : windowMinutes);

	return {
		inLabel: time(day.clockIn),
		left: (from / windowMinutes) * 100,
		outLabel: day.endUnknown || !day.clockOut ? "unknown" : time(day.clockOut),
		show: to > from,
		// A fob scan is the real thing; without one the times come from the first and last status change.
		source: day.clockSource === 20 ? "from status" : day.clockSource === 10 ? "fob" : "",
		width: Math.max(0.4, ((to - from) / windowMinutes) * 100)
	};
}

export function MachineTimeLength(minutes: number): string {
	const total = Math.max(0, Math.round(minutes || 0));
	const hours = Math.floor(total / 60);
	const rest = total % 60;
	return hours > 0 ? `${hours}h${rest > 0 ? " " + rest + "m" : ""}` : `${rest}m`;
}

// The running total beside each day, and which cells it was spread across.
export function MachineTimeSummary(day: any) {
	if (!day.segments || day.segments.length === 0) {
		return day.clockIn ? "no machine status" : "";
	}

	const running = day.segments
		.filter((s: any) => s.status === ProductionStatus.Active)
		.reduce((total: number, s: any) => total + s.minutes, 0);

	return `${MachineTimeLength(running)} running \u00b7 ${day.machines}`;
}
